import { useState } from 'react';
import { Plus, Minus, ShoppingCart, Package } from 'lucide-react';
import type { Articulo } from '@/types';

interface ProductCardProps {
  articulo: Articulo;
  onAdd: (cantidad: number) => void;
  isFrequent?: boolean;
  quantityInCart?: number;
  variant?: 'compact' | 'full';
}

export default function ProductCard({
  articulo,
  onAdd,
  isFrequent = false,
  quantityInCart = 0,
  variant = 'full',
}: ProductCardProps) {
  const [isAnimating, setIsAnimating] = useState(false);

  const handleAddToCart = () => {
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 600);
    onAdd(1);
  };

  const handleIncrement = () => {
    onAdd(1);
  };

  const handleDecrement = () => {
    onAdd(-1);
  };

  const isCaja = articulo.codigo.charAt(3)?.toUpperCase() === 'C';

  if (variant === 'compact') {
    return (
      <div
        className={`
          relative min-w-[150px] rounded-lg bg-white p-3 shadow-md transition-all duration-300 hover:shadow-xl dark:bg-gray-800
          ${isAnimating ? 'animate-pulse-once scale-95' : ''}
        `}
      >
        {isCaja && (
          <div className="absolute right-2 top-2 flex items-center gap-0.5 rounded-md bg-amber-500 px-1.5 py-0.5 text-white shadow-sm">
            <Package className="h-3 w-3" />
            <span className="text-[9px] font-bold">CAJA</span>
          </div>
        )}

        <p className="mb-1 pr-12 text-center text-[10px] font-mono text-gray-500 dark:text-gray-400">
          {articulo.codigo}
        </p>

        <h4 className="mb-3 h-10 line-clamp-2 text-xs font-semibold text-gray-900 dark:text-white">
          {articulo.nombre}
        </h4>

        {quantityInCart === 0 ? (
          <button
            onClick={handleAddToCart}
            className="w-full rounded-lg border border-[#27648d] bg-[#27648d] py-2 text-xs font-bold text-white transition-all hover:border-[#1f5479] hover:bg-[#1f5479]"
            type="button"
          >
            Añadir
          </button>
        ) : (
          <div className="flex items-center justify-between rounded-lg border border-[#27648d] bg-[#27648d] p-1">
            <button
              onClick={handleDecrement}
              className="flex h-7 w-7 items-center justify-center rounded-md bg-white transition-colors hover:bg-gray-100"
              type="button"
            >
              <Minus className="h-4 w-4 text-gray-700" />
            </button>
            <span className="px-2 text-sm font-bold text-white">{quantityInCart}</span>
            <button
              onClick={handleIncrement}
              className="flex h-7 w-7 items-center justify-center rounded-md bg-white transition-colors hover:bg-gray-100"
              type="button"
            >
              <Plus className="h-4 w-4 text-gray-700" />
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`
        relative rounded-lg bg-white p-4 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl dark:bg-gray-800
        ${isAnimating ? 'animate-pulse-once scale-95' : ''}
      `}
    >
      {isCaja && (
        <div className="absolute right-3 top-3 flex items-center gap-0.5 rounded-md bg-amber-500 px-1.5 py-0.5 text-white shadow-sm">
          <Package className="h-3 w-3" />
          <span className="text-[9px] font-bold">CAJA</span>
        </div>
      )}

      <div className="flex min-h-[92px] flex-col">
        <div className="mb-2 pr-16">
          <p className="text-xs font-mono font-semibold text-gray-500 dark:text-gray-400">
            {articulo.codigo}
          </p>
          <h4 className="line-clamp-2 text-sm font-bold leading-tight text-gray-900 dark:text-white">
            {articulo.nombre}
          </h4>
          <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">{articulo.unidadMedida || 'ud'}</p>
        </div>

        <div className="mt-auto flex items-center justify-end">
          {quantityInCart === 0 ? (
            <button
              onClick={handleAddToCart}
              className="flex items-center gap-1 rounded-lg border border-[#27648d] bg-[#27648d] px-6 py-2 text-sm font-bold text-white shadow-md transition-all hover:border-[#1f5479] hover:bg-[#1f5479] hover:shadow-lg"
              type="button"
            >
              <ShoppingCart className="h-4 w-4" />
              Añadir
            </button>
          ) : (
            <div className="flex items-center gap-2 rounded-lg bg-gray-100 p-1 dark:bg-gray-700">
              <button
                onClick={handleDecrement}
                className="flex h-8 w-8 items-center justify-center rounded-lg bg-white transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-600"
                type="button"
              >
                <Minus className="h-4 w-4 text-gray-700 dark:text-gray-300" />
              </button>
              <span className="w-8 text-center text-sm font-bold text-gray-900 dark:text-white">
                {quantityInCart}
              </span>
              <button
                onClick={handleIncrement}
                className="flex h-8 w-8 items-center justify-center rounded-lg bg-white transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-600"
                type="button"
              >
                <Plus className="h-4 w-4 text-gray-700 dark:text-gray-300" />
              </button>
            </div>
          )}
        </div>

        {isFrequent && (
          <div className="mt-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-1 text-xs font-semibold text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
              Habitual
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
